import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ArrowLeft, FileCheck2, Download, Eye, Search, X as XIcon, ExternalLink, Copy } from "lucide-react";
import { EmptyState, PageHeader, formatDateTime, formatDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase as _sb } from "@/lib/supabase";
import { fetchAndSave } from "@/lib/downloads";

/**
 * FormResponses — audit-only list of submissions for a single template.
 * Uses the shared List → Drawer pattern; responses are IMMUTABLE:
 *   • No Edit — no PATCH endpoint exists for form_responses.
 *   • No Delete — deleting the template is the only path (and that path lives
 *     on the parent Forms page with a heavy confirm).
 *   • Drawer clearly labels the record type as "Submitted Response · Audit record".
 */
export default function FormResponses() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailResp, setDetailResp] = useState(null);   // ← drawer state
  const [q, setQ] = useState(params.get("q") || "");

  // Persist search in URL
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q]);

  const load = async () => {
    setLoading(true);
    const [f, r] = await Promise.all([
      supabase.from("form_templates").select("*").eq("id", formId).limit(1),
      supabase.from("form_responses").select("*, clients(id, first_name, last_name)")
        .eq("form_template_id", formId).order("submitted_at", { ascending: false }),
    ]);
    setForm(f.data?.[0] || null);
    setResponses(r.data || []);
    setLoading(false);
  };

  useEffect(() => { if (formId) load(); /* eslint-disable-next-line */ }, [formId]);

  // Deep-link: ?id=xxx opens the corresponding response drawer.
  useEffect(() => {
    const id = params.get("id");
    if (!id || !responses.length || detailResp) return;
    const r = responses.find((x) => x.id === id);
    if (r) { setDetailResp(r); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [responses, params.get("id")]);

  const filtered = useMemo(() => responses.filter((r) => {
    if (!q) return true;
    const hay = [
      r.clients?.first_name, r.clients?.last_name,
      r.submitted_by_name, r.submitted_by_email,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [responses, q]);

  const exportCsv = () => {
    if (!form || responses.length === 0) return;
    const cols = (form.fields || []).map((f) => f.label);
    const header = ["Submitted at", "Client", "Submitter name", "Submitter email", ...cols];
    const rows = responses.map((r) => [
      r.submitted_at,
      r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "",
      r.submitted_by_name || "",
      r.submitted_by_email || "",
      ...(form.fields || []).map((f) => r.responses?.[f.id || f.label] || ""),
    ]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${form.name}-responses.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyAnswers = async (r) => {
    if (!form) return;
    const lines = (form.fields || []).map((f) => {
      const val = r.responses?.[f.id || f.label];
      return `${f.label}: ${val ?? "—"}`;
    });
    const header = [
      `Form: ${form.name}`,
      `Submitted: ${formatDateTime(r.submitted_at)}`,
      r.clients ? `Client: ${r.clients.first_name} ${r.clients.last_name}` : `Submitter: ${r.submitted_by_name || "Anonymous"}`,
      r.submitted_by_email ? `Email: ${r.submitted_by_email}` : null,
      "",
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(header + lines.join("\n"));
      toast.success("Response copied to clipboard");
    } catch { toast.error("Could not copy"); }
  };

  if (loading) return <div className="text-center py-16 text-[#57534E]">Loading responses…</div>;
  if (!form) return <div className="text-center py-16 text-[#57534E]">Form not found.</div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="form-responses-page">
      <Link to={`/app/forms?id=${form.id}`} className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#E8712C]" data-testid="responses-back-link">
        <ArrowLeft size={14} /> Back to forms
      </Link>
      <PageHeader
        title={form.name}
        subtitle={
          <span>
            <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 mr-1.5">Submitted responses</span>
            {responses.length} response{responses.length === 1 ? "" : "s"} · Audit trail — responses are not editable.
          </span>
        }
        actions={responses.length > 0 && (
          <Button data-testid="export-csv-btn" onClick={exportCsv} variant="outline" className="rounded-full"><Download size={13} className="mr-1.5" /> Export CSV</Button>
        )}
      />

      {responses.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <Input data-testid="responses-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by client or submitter…" className="pl-9 bg-white border-[#E8E2D9]" />
          </div>
        </div>
      )}

      {responses.length === 0 ? (
        <EmptyState
          icon={FileCheck2}
          title="No responses yet"
          description="Share this form to start collecting responses from clients or prospects."
          testid="empty-responses"
        />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#57534E] bg-white rounded-2xl border border-[#E8E2D9]">No responses match your search.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden divide-y divide-[#E8E2D9]">
          {filtered.map((r) => (
            <div
              key={r.id}
              data-testid={`response-row-${r.id}`}
              onClick={() => setDetailResp(r)}
              className="flex items-center gap-4 px-6 py-4 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-700 text-sm font-bold flex-shrink-0">
                {(r.clients?.first_name?.[0] || r.submitted_by_name?.[0] || "?").toUpperCase()}
                {(r.clients?.last_name?.[0] || "").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : (r.submitted_by_name || "Anonymous submission")}
                </div>
                <div className="text-xs text-[#57534E] truncate">
                  Submitted {formatDateTime(r.submitted_at)}
                  {r.submitted_by_email && ` · ${r.submitted_by_email}`}
                </div>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hidden sm:inline">Response</span>
              <button
                data-testid={`view-response-${r.id}`}
                onClick={(e) => { e.stopPropagation(); setDetailResp(r); }}
                className="text-xs px-2 py-1 rounded-full text-[#57534E] hover:bg-[#FAF8F5] flex items-center gap-1"
              >
                <Eye size={12} /> View
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ─── Submitted Response Drawer (audit-only) ─────────────────── */}
      <Sheet open={!!detailResp} onOpenChange={(v) => { if (!v) setDetailResp(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="response-drawer">
          {detailResp && (() => {
            const r = detailResp;
            const c = r.clients;
            return (
              <>
                <SheetHeader className="pr-8">
                  <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]" data-testid="response-drawer-record-type">Submitted Response · Audit record</div>
                  <SheetTitle className="font-display tracking-tight text-2xl leading-tight" data-testid="response-drawer-title">
                    {c ? `${c.first_name} ${c.last_name}` : (r.submitted_by_name || "Anonymous submission")}
                  </SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex gap-2 flex-wrap items-center text-xs text-[#57534E]">
                      <span data-testid="response-drawer-submitted-at">Submitted {formatDateTime(r.submitted_at)}</span>
                      {r.submitted_by_email && <span>· {r.submitted_by_email}</span>}
                    </div>
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  <div className="grid grid-cols-2 gap-4 pt-1 border-t border-[#E8E2D9]">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Form template</div>
                      <div className="text-sm">{form.name}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Status</div>
                      <div className="text-sm capitalize">{r.status || "submitted"}</div>
                    </div>
                    {c && (
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Client</div>
                        <button
                          onClick={() => { navigate(`/app/clients/${c.id}`); setDetailResp(null); }}
                          className="text-sm text-[#B85C1F] hover:underline"
                          data-testid="response-drawer-client"
                        >
                          {c.first_name} {c.last_name}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t border-[#E8E2D9]">
                    <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-2">Answers</div>
                    <div className="space-y-3" data-testid="response-drawer-answers">
                      {(form.fields || []).map((f, i) => (
                        <div key={f.id || i}>
                          <div className="text-xs text-[#A8A29E] uppercase tracking-widest">{f.label}</div>
                          <div className="text-sm text-[#1C1917] mt-0.5 whitespace-pre-wrap">
                            {r.responses?.[f.id || f.label] || <span className="text-[#A8A29E]">—</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#E8E2D9] flex items-center gap-2 flex-wrap">
                    <Button
                      data-testid="response-drawer-pdf-btn"
                      onClick={async () => {
                        try {
                          const { data: { session } } = await _sb.auth.getSession();
                          if (!session) return toast.error("Please sign in again");
                          const slug = (form?.name || "response").replace(/\s+/g, "-").toLowerCase();
                          await fetchAndSave(
                            `${process.env.REACT_APP_BACKEND_URL}/api/form-responses/${r.id}/pdf`,
                            { token: session.access_token, filename: `${slug}-response.pdf` }
                          );
                        } catch (e) { toast.error(e.message || "PDF download failed"); }
                      }}
                      className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"
                    >
                      <Download size={13} className="mr-1.5" /> Download PDF
                    </Button>
                    {c && (
                      <Button
                        data-testid="response-drawer-open-client-btn"
                        onClick={() => { navigate(`/app/clients/${c.id}`); setDetailResp(null); }}
                        variant="outline" className="rounded-full"
                      >
                        <ExternalLink size={13} className="mr-1.5" /> Open client
                      </Button>
                    )}
                    <Button
                      data-testid="response-drawer-copy-btn"
                      onClick={() => copyAnswers(r)}
                      variant="outline" className="rounded-full"
                    >
                      <Copy size={13} className="mr-1.5" /> Copy answers
                    </Button>
                    <Button data-testid="response-drawer-close-btn" onClick={() => setDetailResp(null)} variant="ghost" className="rounded-full ml-auto">
                      <XIcon size={13} className="mr-1" /> Close
                    </Button>
                  </div>

                  <div className="pt-3 border-t border-[#E8E2D9]">
                    <div className="text-[10px] text-[#A8A29E] italic">
                      Responses are part of the audit trail and cannot be edited or deleted individually.
                      To remove them, delete the parent template from the Forms page — that will permanently
                      remove every submission for it.
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
